import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckoutApiError, checkoutApi } from "../api/client";
import type {
  ConfirmResult,
  PublicCheckoutCustomer,
  PublicCheckoutSession,
  PublicPaymentMethod,
} from "../api/types";
import { CustomerForm } from "../components/CustomerForm";
import { PaymentMethodSelector } from "../components/PaymentMethodSelector";
import { QrView } from "../components/QrView";
import { SessionSummary } from "../components/SessionSummary";
import { botonDeMarca } from "../utils/brand";
import { StatusView } from "../components/StatusView";

type ScreenState =
  | { kind: "loading" }
  | { kind: "ready"; session: PublicCheckoutSession }
  | { kind: "submitting"; session: PublicCheckoutSession }
  | { kind: "result"; session: PublicCheckoutSession; result: ConfirmResult }
  | { kind: "session_invalid" }
  | { kind: "network_error"; retry: () => void };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Estrategia para pagos con QR directo (ej. Dpago). Se decide por sesión con
 * `session.uiConfig.qrEmbedded`, que el ERP setea desde la config de la empresa
 * (config avanzada "QR embebido"). Dpago devuelve SIEMPRE `redirectUrl` (su
 * página hospedada) y, para QR en producción, también `qrInformation` (payload
 * EMV para dibujar nosotros).
 *
 * - `false` (default): usamos `redirectUrl` → el cliente va a la página del
 *   proveedor, que ya resuelve QR, cuenta regresiva, estado y "descargar".
 * - `true`: si el confirm trae `qr`, lo mostramos EMBEBIDO acá (QrView lo dibuja)
 *   y dejamos `redirectUrl` como respaldo.
 */
function prefersEmbeddedQr(session: PublicCheckoutSession | null): boolean {
  return session?.uiConfig?.qrEmbedded === true;
}

export function CheckoutPage() {
  const { publicToken } = useParams<{ publicToken: string }>();
  const [screen, setScreen] = useState<ScreenState>({ kind: "loading" });
  const [customer, setCustomer] = useState<PublicCheckoutCustomer>({});
  const [errors, setErrors] = useState<Partial<Record<keyof PublicCheckoutCustomer, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [methods, setMethods] = useState<PublicPaymentMethod[]>([]);
  const [platformId, setPlatformId] = useState<string | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!publicToken) {
        setScreen({ kind: "session_invalid" });
        return;
      }
      setScreen({ kind: "loading" });
      checkoutApi
        .getSession(publicToken, signal)
        .then((session) => {
          if (session.status !== "open") {
            setScreen({ kind: "session_invalid" });
          } else {
            // Si el merchant ya creó un Customer al iniciar el cobro,
            // prefill el form con esos datos. Solo se aplica una vez al
            // cargar — si el comprador edita los inputs, ganan los suyos.
            if (session.customer) {
              setCustomer({
                email: session.customer.email ?? undefined,
                name: session.customer.name ?? undefined,
                docNumber: session.customer.docNumber ?? undefined,
                phone: session.customer.phone ?? undefined,
                country: session.customer.country ?? undefined,
              });
            }
            setScreen({ kind: "ready", session });
            // Los métodos son secundarios: si fallan, seguimos sin selector
            // (el provider elige en su checkout / usa su default).
            checkoutApi
              .paymentMethods(publicToken, signal)
              .then((list) => {
                setMethods(list);
                if (list.length === 1) setPlatformId(list[0].platformId);
              })
              .catch(() => setMethods([]));
          }
        })
        .catch((err: CheckoutApiError) => {
          if (signal?.aborted) return;
          if (err.statusCode === 404) {
            setScreen({ kind: "session_invalid" });
          } else {
            setScreen({ kind: "network_error", retry: () => load() });
          }
        });
    },
    [publicToken],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const session = useMemo(() => {
    if (screen.kind === "ready" || screen.kind === "submitting" || screen.kind === "result") {
      return screen.session;
    }
    return null;
  }, [screen]);

  function validate(): boolean {
    const next: typeof errors = {};
    if (!customer.email) {
      next.email = "El email es obligatorio.";
    } else if (!EMAIL_RE.test(customer.email)) {
      next.email = "Formato de email inválido.";
    }
    setErrors(next);
    if (methods.length > 0 && !platformId) {
      setSubmitError("Elegí un medio de pago para continuar.");
      return false;
    }
    return Object.keys(next).length === 0;
  }

  async function handleConfirm() {
    if (!publicToken || !session) return;
    if (!validate()) return;
    setSubmitError(null);
    setScreen({ kind: "submitting", session });
    try {
      const result = await checkoutApi.confirm(publicToken, {
        customer,
        platformId: platformId ?? undefined,
      });
      // Modo redirect (default): si el provider devolvió un link, lo seguimos.
      // Modo embebido (PREFER_EMBEDDED_QR): si además hay QR inline, NO redirigimos
      // y lo mostramos acá; el redirect queda como respaldo en la pantalla de
      // resultado. Si no hay QR, siempre redirigimos.
      const embedQr = prefersEmbeddedQr(session) && !!result.qr;
      if (result.redirectUrl && !embedQr) {
        window.location.assign(result.redirectUrl);
        return;
      }
      setScreen({ kind: "result", session, result });
    } catch (err) {
      const apiErr = err as CheckoutApiError;
      if (apiErr.statusCode === 404) {
        setScreen({ kind: "session_invalid" });
      } else if (apiErr.statusCode === 400) {
        setSubmitError(apiErr.message);
        setScreen({ kind: "ready", session });
      } else if (apiErr.statusCode === 429) {
        setSubmitError("Demasiados intentos. Esperá un minuto y volvé a probar.");
        setScreen({ kind: "ready", session });
      } else {
        setSubmitError("No pudimos procesar tu pago. Probá nuevamente en unos segundos.");
        setScreen({ kind: "ready", session });
      }
    }
  }

  if (screen.kind === "loading") {
    return (
      <CenteredPaper>
        <Stack alignItems="center" py={6}>
          <CircularProgress />
        </Stack>
      </CenteredPaper>
    );
  }

  if (screen.kind === "session_invalid") {
    return (
      <CenteredPaper>
        <StatusView status="session_invalid" />
      </CenteredPaper>
    );
  }

  if (screen.kind === "network_error") {
    return (
      <CenteredPaper>
        <StatusView
          status="network_error"
          primaryAction={{ label: "Reintentar", onClick: screen.retry }}
        />
      </CenteredPaper>
    );
  }

  if (screen.kind === "result") {
    const { result } = screen;
    // Si el provider devolvió un QR inline (sin redirect), lo mostramos acá.
    if (result.qr) {
      return (
        <CenteredPaper>
          <Stack spacing={2}>
            <SessionSummary session={screen.session} />
            <Divider />
            <QrView qr={result.qr} />
            {result.redirectUrl ? (
              <Button variant="outlined" size="small" href={result.redirectUrl}>
                Abrir la página de pago
              </Button>
            ) : null}
            {result.returnUrl ? (
              <Button variant="text" size="small" href={result.returnUrl}>
                Volver al comercio
              </Button>
            ) : null}
          </Stack>
        </CenteredPaper>
      );
    }
    return (
      <CenteredPaper>
        <StatusView
          status={result.status}
          secondaryAction={
            result.returnUrl
              ? { label: "Volver al comercio", href: result.returnUrl }
              : undefined
          }
        />
      </CenteredPaper>
    );
  }

  // ready | submitting
  const submitting = screen.kind === "submitting";
  const current = screen.session;
  return (
    <CenteredPaper>
      <Stack spacing={3}>
        <SessionSummary session={current} />
        <Divider />
        <CustomerForm value={customer} errors={errors} disabled={submitting} onChange={setCustomer} />
        {methods.length > 0 ? (
          <PaymentMethodSelector
            methods={methods}
            value={platformId}
            disabled={submitting}
            onChange={(id) => {
              setPlatformId(id);
              setSubmitError(null);
            }}
          />
        ) : null}
        {submitError ? <Alert severity="error">{submitError}</Alert> : null}
        <Button
          variant="contained"
          size="large"
          onClick={handleConfirm}
          disabled={submitting}
          sx={botonDeMarca(current.uiConfig?.brandColor)}
        >
          {submitting ? <CircularProgress size={22} color="inherit" /> : "Pagar"}
        </Button>
        {current.cancelUrl ? (
          <Button
            variant="text"
            size="small"
            href={current.cancelUrl}
            rel="noopener noreferrer"
            disabled={submitting}
          >
            Cancelar y volver al comercio
          </Button>
        ) : null}
      </Stack>
    </CenteredPaper>
  );
}

function CenteredPaper({ children }: { children: React.ReactNode }) {
  return (
    <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          border: "1px solid",
          borderColor: "rgba(15, 23, 42, 0.06)",
          boxShadow:
            "0 1px 2px rgba(15, 23, 42, 0.04), 0 12px 40px -12px rgba(15, 23, 42, 0.18)",
        }}
      >
        <Box>{children}</Box>
      </Paper>
    </Container>
  );
}
